import React, { useEffect, useState } from 'react';
import { ILineAmendmentLovs, ILineAmendmentProps } from '.';
import AmendmentDetails from '../components/AmendmentDetails';
import Loader from '@components/loader/Loader';
import { cloneDeep } from 'lodash';
import { getAmendmentInitialState } from '../content';
import {
  convertToPolicyPlanProposalCovers,
  convertToPolicyPropertyDetails,
  mapToAmendmentDetails,
} from '../utils';
import { useLazyQuery } from '@apollo/client';
import {
  getBBBDetailsQuery,
  getCoversPersonsQuery,
  getPropertyRiskDetailsQuery,
  getMarineDetailsQuery,
  getMarineHullDetailsQuery,
} from '../queries';
import { initialCoverValues } from '../components/content';
import { convertToPolicyPersons } from '../components/policy-persons-widgets/utils';
import PolicyCovers from '../components/PolicyCovers';
import CostsAndCharges from '../components/CostsAndCharges';
import InsuredWidget from '../components/policy-persons-widgets/InsuredWidget';
import PayerOwnerWidget from '../components/policy-persons-widgets/PayerOwnerWidget';
import BeneficiaryWidget from '../components/policy-persons-widgets/BeneficiaryWidget';
import PropertyDescriptionDetailsWidget from '../components/PropertyDescription';
import RiskDescription from '../components/RiskDescription';
import BankersDetails from '../components/BankersDetails';
import MarineDetails from '../components/MarineDetails';
import MarineHullDetails from '../components/MarineHullDetails';
import {
  enumListAsRecordObject,
  lookupListAsRecordObject,
} from '@utils/graph-utils';
import { IAbstractRecord } from '@models';
import {
  GenericLines,
  getGenericLine,
  hasProperty,
  hasRisk,
} from '@utils/helper-utils';
import AmendmentPolicyDetails from '../components/AmendmentPolicyDetails';

interface IGenericAmendmentLovs extends ILineAmendmentLovs {
  geoLocations: Record<string, string>;
  matters: Record<string, string>;
}

const GenericAmendment: React.FC<ILineAmendmentProps> = ({
  generalData,
  data,
  pageState,
  handleStateChange,
}) => {
  const amendmentData = data?.Production?.queries?.getAmendmentDetails;
  const isGroupPolicy = generalData.policy.type === 'GROUP';
  const genericLine = getGenericLine(generalData.line.externalCode);
  const [loading, setLoading] = useState<boolean>();
  const isDisabled = ['COMPLETED', 'CANCELLED', 'EXPIRED'].includes(
    generalData.amendment.status
  );

  const [lovs, setLovs] = useState<IGenericAmendmentLovs>({
    amendmentTypes: {},
    amendmentSubTypes: {},
    geoLocations: {},
    matters: {},
  });

  const [getCoversPersonsLazy] = useLazyQuery(getCoversPersonsQuery('both'));

  const [getPropertyRiskDetailsLazy] = useLazyQuery(
    getPropertyRiskDetailsQuery,
    {
      variables: {
        selectedAmendmentID: generalData.amendment.id,
        selectedProposalID: generalData.proposal.id,
        version: generalData.policy.version,
      },
      errorPolicy: 'all',
    }
  );

  const [getBBBDetailsLazy] = useLazyQuery(getBBBDetailsQuery, {
    variables: {
      selectedPolicyID: generalData.policy.id,
      version: generalData.policy.version,
    },
    errorPolicy: 'all',
  });

  const [getMarineDetailsLazy] = useLazyQuery(getMarineDetailsQuery, {
    variables: {
      selectedAmendmentID: generalData.amendment.id,
      version: generalData.policy.version,
    },
    errorPolicy: 'all',
  });

  const [getMarineHullDetailsLazy] = useLazyQuery(getMarineHullDetailsQuery, {
    variables: {
      selectedAmendmentID: generalData.amendment.id,
      version: generalData.policy.version,
    },
    errorPolicy: 'all',
  });

  const initialize = async () => {
    setLoading(true);

    let detailsData: IAbstractRecord;

    if (genericLine === GenericLines.BBB) {
      const { data: responseData } = await getBBBDetailsLazy();
      detailsData = {
        ...amendmentData?.[0],
        bankersDetails:
          responseData.Production.queries.getPolicyBankersBlanketBondDetails,
      };
    } else if (
      genericLine === GenericLines.MARINE &&
      generalData.policy.type !== 'GROUP'
    ) {
      const { data: responseData } = await getMarineDetailsLazy();
      detailsData = {
        ...amendmentData?.[0],
        ...responseData.Production.queries.getAmendmentDetails[0],
      };
    } else if (genericLine === GenericLines.MARINE_HULL) {
      const { data: responseData } = await getMarineHullDetailsLazy();
      detailsData = {
        ...amendmentData?.[0],
        ...responseData.Production.queries.getAmendmentDetails[0],
      };
    } else {
      // Fallback in case none of the above apply
      detailsData = amendmentData?.[0] || {};
    }

    const newValues = mapToAmendmentDetails(detailsData);

    if (hasProperty(generalData.line.externalCode)) {
      const { data: propData } = await getPropertyRiskDetailsLazy();
      newValues.riskDetails = convertToPolicyPropertyDetails(
        propData.Production.queries.getPolicyPropertyDetails
      );
    }
    const newErrors = cloneDeep(
      getAmendmentInitialState(generalData.line.category).errors
    );
    const newTouched = cloneDeep(
      getAmendmentInitialState(generalData.line.category).touched
    );

    // get covers and/or persons --start
    const { data: coversPersonsData } = await getCoversPersonsLazy({
      variables: {
        id: generalData.proposal.id,
        version: generalData.policy.version,
      },
      errorPolicy: 'all',
    });

    const covers = convertToPolicyPlanProposalCovers(
      coversPersonsData,
      amendmentData
    );
    newValues.covers = covers;
    newErrors.covers = covers?.map(() => initialCoverValues.initialError);
    newTouched.covers = covers?.map(() => initialCoverValues.initialTouched);

    const persons = convertToPolicyPersons(coversPersonsData);
    newValues.policyPersons = persons;
    newErrors.policyPersons = {
      owner: newErrors['policyPersons'].owner,
      payer: newErrors['policyPersons'].payer,
      insured: persons.insured.map(() => newErrors['policyPersons'].insured[0]),
      beneficiaries: persons.beneficiaries.map(
        () => newErrors['policyPersons'].beneficiaries[0]
      ),
    };
    newTouched.policyPersons = {
      owner: newTouched['policyPersons'].owner,
      payer: newTouched['policyPersons'].payer,
      insured: persons.insured.map(
        () => newTouched['policyPersons'].insured[0]
      ),
      beneficiaries: persons.beneficiaries.map(
        () => newTouched['policyPersons'].beneficiaries[0]
      ),
    };
    // get covers and/or persons --end

    setLovs({
      matters:
        genericLine === GenericLines.MARINE
          ? lookupListAsRecordObject(detailsData?.Core?.lookups?.matters)
          : {},
      geoLocations: hasProperty(generalData.line.externalCode)
        ? lookupListAsRecordObject(detailsData?.Core?.lookups?.geoLocations)
        : {},
      amendmentTypes: enumListAsRecordObject(
        data.Production_AmendmentTypes.enumValues
      ),
      amendmentSubTypes: lookupListAsRecordObject(
        data.Production.lookups.amendmentSubTypes,
        false,
        'Code',
        'Title'
      ),
    });

    handleStateChange({
      values: newValues,
      errors: newErrors,
      touched: newTouched,
    });
    setLoading(false);
  };

  useEffect(() => {
    if (data) {
      initialize();
    }
  }, [data]);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <AmendmentDetails
        generalData={generalData}
        lovs={lovs}
        pageState={pageState}
        onChange={handleStateChange}
        isDisabled={isDisabled}
      />
      <AmendmentPolicyDetails
        generalData={generalData}
        lovs={lovs}
        pageState={pageState}
        onChange={handleStateChange}
        isDisabled={isDisabled}
      />
      <PolicyCovers
        generalData={generalData}
        pageState={pageState}
        onChange={handleStateChange}
        isDisabled={isDisabled}
        isGroupPolicy={isGroupPolicy}
      />
      {hasProperty && (
        <PropertyDescriptionDetailsWidget
          generalData={generalData}
          pageState={pageState}
          onPageStateUpdate={handleStateChange}
          isDisabled={isDisabled}
          lovs={{
            geoLocations: lovs.geoLocations,
            planCovers: {}, // TODO: get plan covers
          }}
        />
      )}
      <CostsAndCharges
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isDisabled}
      />
      {hasRisk(generalData.line.externalCode) && (
        <RiskDescription
          pageState={pageState}
          onPageStateUpdate={handleStateChange}
          isDisabled={isDisabled}
          geoLocationsLov={lovs.geoLocations}
        />
      )}
      <InsuredWidget
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isDisabled}
        maxNumber={
          amendmentData?.[0]?.policy_PlanID?.views?.PlanConfigManagement_all
            ?.properties?.MaxNbInsured
        }
      />
      <PayerOwnerWidget
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isDisabled}
      />
      <BeneficiaryWidget
        generalData={generalData}
        pageState={pageState}
        onPageStateUpdate={handleStateChange}
        isDisabled={isDisabled}
        maxNumber={
          amendmentData?.[0]?.policy_PlanID?.views?.PlanConfigManagement_all
            ?.properties?.MaxNbBeneficiary
        }
      />
      {genericLine === GenericLines.BBB && (
        <BankersDetails
          generalData={generalData}
          pageState={pageState}
          onPageStateUpdate={handleStateChange}
          isDisabled={isDisabled}
        />
      )}
      {!isGroupPolicy && genericLine === GenericLines.MARINE && (
        <MarineDetails
          generalData={generalData}
          pageState={pageState}
          onPageStateUpdate={handleStateChange}
          isDisabled={isDisabled}
          mattersLov={lovs.matters}
        />
      )}
      {genericLine === GenericLines.MARINE_HULL && (
        <MarineHullDetails
          generalData={generalData}
          pageState={pageState}
          onPageStateUpdate={handleStateChange}
          isDisabled={isDisabled}
        />
      )}
    </>
  );
};

export default GenericAmendment;
